import { supabase } from '@/integrations/supabase/client';
import { generateSwissPayslip, type SwissPayslipData, type TimeSubmissionData } from './swissPayslipGenerator';
import { format } from 'date-fns';
import { round2, secondsToHours, calculateMonthlySoll, deriveContractParams } from '@/utils/workTimeUtils';
import { getEmployeeOverride } from '@/config/employeeOverrides';
import { COMPANY_INFO } from '@/config/companyInfo';
import { PAYSLIP_MIN_YEAR, PAYSLIP_MIN_MONTH, getCurrentPayslipMonth, isValidPayslipMonth } from '@/config/payslipConfig';


// ─── Types ──────────────────────────────────────────────────────────

export interface PayslipData {
  employeeId: string;
  employeeName: string;
  month: string;
  year: string;
  grossSalary: number;
  netSalary: number;
  workingHours?: number;
  hourlyRate?: number;
  monthlySalary?: number;
  vacationDays?: number;
  paymentModel: 'hourly' | 'monthly';
}

// ─── Time submission helpers ────────────────────────────────────────

/** Fetch actual hours worked from time_entries for a specific month. */
async function fetchMonthlyHoursWorked(
  employeeId: string,
  year: number,
  month: number
): Promise<number> {
  const mm = month.toString().padStart(2, '0');
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const nextMM = nextMonth.toString().padStart(2, '0');

  const { data, error } = await supabase
    .from('time_entries')
    .select('duration')
    .eq('user_id', employeeId)
    .is('deleted_at', null)
    .gte('start_time', `${year}-${mm}-01`)
    .lt('start_time', `${nextYear}-${nextMM}-01`);

  if (error) {
    console.error('Error fetching time entries for payslip:', error);
    return 0;
  }

  const totalSeconds = (data || []).reduce((sum, e) => sum + (e.duration || 0), 0);
  return round2(secondsToHours(totalSeconds));
}

/** Fetch vacation hours from vacation_transactions for a specific month. */
async function fetchMonthlyVacationHours(
  employeeId: string,
  year: number,
  month: number
): Promise<number> {
  const mm = month.toString().padStart(2, '0');
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const nextMM = nextMonth.toString().padStart(2, '0');

  const { data, error } = await supabase
    .from('vacation_transactions')
    .select('days_amount')
    .eq('employee_id', employeeId)
    .eq('transaction_type', 'taken')
    .gte('transaction_date', `${year}-${mm}-01`)
    .lt('transaction_date', `${nextYear}-${nextMM}-01`);

  if (error) {
    console.error('Error fetching vacation hours for payslip:', error);
    return 0;
  }

  return round2((data || []).reduce((sum, t) => sum + (t.days_amount || 0), 0));
}

/** Build the TimeSubmissionData for a given month. */
async function buildTimeSubmission(
  employeeId: string,
  employee: Parameters<typeof deriveContractParams>[0] & { email?: string | null },
  year: number,
  month: number
): Promise<TimeSubmissionData> {
  const [actualHours, vacationHours] = await Promise.all([
    fetchMonthlyHoursWorked(employeeId, year, month),
    fetchMonthlyVacationHours(employeeId, year, month),
  ]);

  const overrides = employee.email ? getEmployeeOverride(employee.email) : undefined;
  const { monthlyTarget, canton, contractStart, contractEnd } = deriveContractParams(employee, overrides);

  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  // If employment ends mid-month, prorate Soll/working days up to the last working day.
  const endsThisMonth = !!contractEnd && contractEnd >= monthStart && contractEnd < monthEnd;

  const sollResult = calculateMonthlySoll(monthlyTarget, year, month, canton, {
    includeClosures: true,
    contractStart:
      contractStart && contractStart > monthStart ? contractStart : undefined,
    currentDate: endsThisMonth ? contractEnd : undefined,
    vacationHours,
  });

  const difference = round2(actualHours - sollResult.soll);

  // Only list holidays up to the last working day in the leaving month.
  const holidays = endsThisMonth
    ? sollResult.holidays.filter((h) => h.date <= contractEnd!)
    : sollResult.holidays;

  return {
    actualHours,
    expectedHours: sollResult.soll,
    difference,
    workingDays: sollResult.workingDays,
    vacationHours,
    holidays: holidays.map((h) => h.name),
  };
}

// ─── Main download function ─────────────────────────────────────────

export const downloadEmployeePayslip = async (
  employeeId: string,
  month: number,
  year: number
): Promise<void> => {
  // Defense-in-depth: UI already filters via getAvailablePayslipMonths,
  // but validate again in case this function is called directly.
  if (!isValidPayslipMonth(year, month)) {
    throw new Error('Lohnabrechnung für diesen Monat nicht verfügbar');
  }

  try {
    // Check for payroll override first
    const { calculateFixedNetPayroll } = await import('./payrollOverrides');
    const fixedResult = await calculateFixedNetPayroll(
      employeeId,
      undefined, // birth_date will be fetched from employee record
      new Date(year, month - 1, 1)
    );

    // Fetch employee data
    const { data: employee, error: empError } = await supabase
      .from('employees')
      .select('*')
      .eq('id', employeeId)
      .single();

    if (empError) throw empError;

    // Use firstname + lastname for the employee's full name on the payslip
    const employeeFullName = [employee.firstname, employee.lastname].filter(Boolean).join(' ') || employee.name;

    let payslipData: PayslipData;
    let isMinimal = false;

    if (fixedResult) {
      // Use fixed net calculation
      payslipData = {
        employeeId: employee.id,
        employeeName: employeeFullName,
        month: month.toString().padStart(2, '0'),
        year: year.toString(),
        grossSalary: fixedResult.gross_salary,
        netSalary: fixedResult.net_salary,
        workingHours: 0,
        hourlyRate: undefined,
        monthlySalary: fixedResult.gross_salary,
        vacationDays: 0,
        paymentModel: 'monthly'
      };
      isMinimal = fixedResult.payslip_minimal;
    } else {
      // Fetch payroll calculation for the specified month/year
      const mm = month.toString().padStart(2, '0');
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const nextMM = nextMonth.toString().padStart(2, '0');

      const { data: payrollData, error: payrollError } = await supabase
        .from('payroll_calculations')
        .select('*')
        .eq('employee_id', employeeId)
        .gte('created_at', `${year}-${mm}-01`)
        .lt('created_at', `${nextYear}-${nextMM}-01`)
        .order('created_at', { ascending: false })
        .limit(1);

      if (payrollError) throw payrollError;

      if (payrollData && payrollData.length > 0) {
        // Use existing payroll calculation
        const calculation = payrollData[0];
        payslipData = {
          employeeId: employee.id,
          employeeName: employeeFullName,
          month: mm,
          year: year.toString(),
          grossSalary: calculation.gross_salary,
          netSalary: calculation.net_salary,
          workingHours: calculation.hours_worked,
          hourlyRate: employee.hourly_rate,
          monthlySalary: employee.monthly_salary,
          vacationDays: calculation.vacation_days_earned,
          paymentModel: employee.work_type === 'hourly' ? 'hourly' : 'monthly'
        };
      } else {
        // Fallback: compute gross from DB fields, compute proper deductions
        const { calculateAllDeductions, calculateNetSalary } = await import('./payrollCalculations');

        const weeklyHours = employee.working_hours_per_week ?? 40;
        const grossSalary = employee.work_type === 'hourly'
          ? (employee.hourly_rate || 0) * weeklyHours * 52 / 12
          : employee.monthly_salary || 0;

        const deductions = calculateAllDeductions(grossSalary, {
          id: employee.id,
          name: employeeFullName,
          salary_type: employee.salary_type || employee.work_type || 'monthly',
          work_type: employee.work_type || 'salaried',
          birth_date: employee.birth_date,
          tax_canton: employee.tax_canton,
          hourly_rate: employee.hourly_rate,
          monthly_salary: employee.monthly_salary,
          working_hours_per_week: employee.working_hours_per_week,
        });
        const netSalary = calculateNetSalary(grossSalary, deductions);

        payslipData = {
          employeeId: employee.id,
          employeeName: employeeFullName,
          month: mm,
          year: year.toString(),
          grossSalary: round2(grossSalary),
          netSalary,
          workingHours: employee.work_type === 'hourly'
            ? round2(weeklyHours * 52 / 12)
            : undefined,
          hourlyRate: employee.hourly_rate,
          monthlySalary: employee.monthly_salary,
          vacationDays: 0,
          paymentModel: employee.work_type === 'hourly' ? 'hourly' : 'monthly'
        };
      }
    }

    // ── Build mandatory time submission (Monatsabschluss) ──
    const timeSubmission = await buildTimeSubmission(
      employeeId,
      employee,
      year,
      month
    );

    // Generate payslip data for the Swiss generator
    const swissPayslipData: SwissPayslipData = {
      employee: {
        id: employee.id,
        name: employeeFullName,
        address: employee.address || '',
        ahv_number: employee.ahv_number || '',
        employee_number: employee.id.substring(0, 8)
      },
      company: COMPANY_INFO,
      payroll: {
        employee_id: payslipData.employeeId,
        gross_salary: payslipData.grossSalary,
        net_salary: payslipData.netSalary,
        total_deductions: payslipData.grossSalary - payslipData.netSalary,
        hours_worked: payslipData.workingHours || 0,
        base_salary: payslipData.monthlySalary || 0,
        vacation_days_taken: payslipData.vacationDays || 0,
        vacation_pay_included: false,
        deductions: fixedResult?.deductions || []
      },
      period: {
        start_date: `${year}-${month.toString().padStart(2, '0')}-01`,
        end_date: `${year}-${month.toString().padStart(2, '0')}-${new Date(year, month, 0).getDate()}`,
        payment_date: `${year}-${month.toString().padStart(2, '0')}-${new Date(year, month, 0).getDate()}`
      },
      timeSubmission,
      minimal: isMinimal
    };

    // Generate and download the payslip
    const blob = await generateSwissPayslip(swissPayslipData);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${year}-${month.toString().padStart(2, '0')}_${employeeFullName.replace(/\s+/g, '_')}_Lohnabrechnung.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error downloading payslip:', error);
    throw error;
  }
};

export const getAvailablePayslipMonths = async (
  employeeId: string,
  options?: { respectCutoff?: boolean }
): Promise<Array<{month: number, year: number}>> => {
  const respectCutoff = options?.respectCutoff ?? true;

  try {
    const { data: employee } = await supabase
      .from('employees')
      .select('employment_start_date')
      .eq('id', employeeId)
      .single();

    if (!employee?.employment_start_date) {
      return [];
    }

    const startDate = new Date(employee.employment_start_date);

    // Start: later of employment_start_date or PAYSLIP_MIN (if respectCutoff)
    let startYear = startDate.getFullYear();
    let startMonth = startDate.getMonth() + 1;
    if (respectCutoff) {
      const minVal = PAYSLIP_MIN_YEAR * 12 + PAYSLIP_MIN_MONTH;
      const empVal = startYear * 12 + startMonth;
      if (empVal < minVal) {
        startYear = PAYSLIP_MIN_YEAR;
        startMonth = PAYSLIP_MIN_MONTH;
      }
    }

    // End: current month in Europe/Zurich timezone
    const { year: endYear, month: endMonth } = getCurrentPayslipMonth();
    const endVal = endYear * 12 + endMonth;

    const months: Array<{month: number, year: number}> = [];
    let iterYear = startYear;
    let iterMonth = startMonth;

    while (iterYear * 12 + iterMonth <= endVal) {
      months.push({ month: iterMonth, year: iterYear });
      iterMonth++;
      if (iterMonth > 12) {
        iterMonth = 1;
        iterYear++;
      }
    }

    return months.reverse(); // Most recent first
  } catch (error) {
    console.error('Error getting available months:', error);
    return [];
  }
};
